import { useCallback, useMemo, useState } from 'react';
import { useAppResource } from '../../context/AppDataContext';
import { deviceService } from '../../services/deviceService';
import DataGrid, { actionsColumn, dash } from '../../components/DataGrid';
import { StatCards } from '../../components/ScheduleKpis';
import BulkImportPanel from '../../components/BulkImportPanel';
import { toast } from '../../utils/toast';
import { daysUntil, deviceKpis } from '../../utils/listKpis';
import './Devices.css';
import { PageLoader } from '../../components/Loader';

export const DEVICE_TYPES = {
  mobile: 'Mobile phone',
  hmc: 'HMC (hand-held scanner)',
  tablet: 'Tablet',
  other: 'Other',
};
const TYPE_SHORT = { mobile: 'Mobile', hmc: 'HMC', tablet: 'Tablet', other: 'Other' };
const OWNERSHIP = { own: 'Own', lease: 'Lease' };
const CONDITIONS = { working: 'Working', repair: 'Under repair', retired: 'Retired' };

// ---- Bulk upload ---------------------------------------------------------------

const DEVICE_COLUMNS = [
  { key: 'device_id', label: 'Device ID / Serial no.', required: true, aliases: ['device id', 'deviceid', 'device', 'serial no', 'serial number', 'serial', 'sr no', 's no', 'imei', 'android id', 'id'] },
  { key: 'label', label: 'Label', aliases: ['name', 'device name', 'nickname'] },
  { key: 'device_type', label: 'Device type', hint: 'Mobile, HMC, Tablet or Other', aliases: ['type', 'device type', 'category'] },
  { key: 'make_model', label: 'Make / model', aliases: ['make', 'model', 'make model', 'brand model', 'brand'] },
  { key: 'ownership', label: 'Ownership', hint: 'Own or Lease', aliases: ['own lease', 'own or lease', 'owned leased', 'owned'] },
  { key: 'condition_status', label: 'Condition', hint: 'Working, Under repair or Retired', aliases: ['status', 'condition', 'health'] },
  { key: 'is_active', label: 'Allowed', hint: 'Yes / No (blank = Yes)', aliases: ['allowed', 'access', 'active', 'enabled'] },
  { key: 'lease_vendor', label: 'Lease vendor', aliases: ['vendor', 'lessor', 'rented from', 'supplier'] },
  { key: 'lease_start', label: 'Lease start', hint: 'YYYY-MM-DD or DD-MM-YYYY', aliases: ['start date', 'lease from', 'from'] },
  { key: 'lease_end', label: 'Lease end', hint: 'YYYY-MM-DD or DD-MM-YYYY', aliases: ['end date', 'lease to', 'to', 'expiry', 'lease expiry'] },
  { key: 'monthly_rent', label: 'Monthly rent', aliases: ['rent', 'monthly rent', 'rent per month', 'rental'] },
  { key: 'notes', label: 'Notes', aliases: ['remarks', 'remark', 'note', 'comments'] },
];
const DEVICE_SAMPLE = [
  { device_id: 'ANDROID-1A2B3C4D', label: 'Store scanner 1', device_type: 'Mobile', make_model: 'Samsung A15', ownership: 'Own', condition_status: 'Working', is_active: 'Yes' },
  { device_id: 'ZT26-22045B1187', label: 'HMC 07', device_type: 'HMC', make_model: 'Zebra TC26', ownership: 'Lease', condition_status: 'Working', is_active: 'Yes', lease_vendor: 'ScanRent', lease_start: '2026-11-01', lease_end: '2027-04-30', monthly_rent: '1500' },
];
const TYPE_WORDS = /^(mobile|phone|mobile phone|smartphone|android|hmc|hand ?held|hand-held|scanner|hand held scanner|pda|tablet|tab|ipad|others?)$/i;
const OWN_WORDS = /^(own|owned|company|purchased|self|lease|leased|rent|rented|rental|hired)$/i;
const COND_WORDS = /^(working|ok|good|active|repair|under repair|in repair|faulty|damaged|retired|scrapped|lost|not in use)$/i;
const YES_NO = /^(yes|y|true|1|allowed|allow|active|no|n|false|0|blocked|block|inactive)$/i;

function checkDeviceRow(row) {
  if (!row.device_id) return 'Device ID / serial no. is missing';
  if (row.device_type && !TYPE_WORDS.test(row.device_type.trim())) return `Device type "${row.device_type}" — use Mobile, HMC, Tablet or Other`;
  if (row.ownership && !OWN_WORDS.test(row.ownership.trim())) return `Ownership "${row.ownership}" — use Own or Lease`;
  if (row.condition_status && !COND_WORDS.test(row.condition_status.trim())) return `Condition "${row.condition_status}" — use Working, Under repair or Retired`;
  if (row.is_active && !YES_NO.test(row.is_active.trim())) return `Allowed "${row.is_active}" — use Yes or No`;
  if (row.monthly_rent && !Number.isFinite(Number(row.monthly_rent.replace(/[₹,\s]/g, '')))) return 'Monthly rent is not a number';
  return '';
}

const emptyForm = {
  device_id: '',
  label: '',
  is_active: true,
  device_type: 'mobile',
  ownership: 'own',
  make_model: '',
  condition_status: 'working',
  lease_vendor: '',
  lease_start: '',
  lease_end: '',
  monthly_rent: '',
  notes: '',
};

const FILTERS = [
  { id: 'all', label: 'All', test: () => true },
  { id: 'mobile', label: 'Mobile', test: (d) => (d.device_type || 'mobile') === 'mobile' },
  { id: 'hmc', label: 'HMC', test: (d) => d.device_type === 'hmc' },
  { id: 'own', label: 'Owned', test: (d) => d.ownership !== 'lease' },
  { id: 'lease', label: 'Leased', test: (d) => d.ownership === 'lease' },
  {
    id: 'ending',
    label: 'Lease ending / expired',
    test: (d) => d.ownership === 'lease' && daysUntil(d.lease_end) !== null && daysUntil(d.lease_end) <= 30,
  },
  { id: 'repair', label: 'Under repair', test: (d) => d.condition_status === 'repair' },
  { id: 'blocked', label: 'Blocked', test: (d) => !d.is_active },
];

function shortDate(value) {
  if (!value) return '';
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number);
  if (!y) return '';
  return new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

function lastUsed(iso) {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const minutes = Math.round((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 24 * 60) return `${Math.round(minutes / 60)} h ago`;
  return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "Ends in 12 days" / "Expired 3 days ago" for a leased device. */
function leaseText(device) {
  if (device.ownership !== 'lease') return '';
  const days = daysUntil(device.lease_end);
  if (days === null) return 'No end date';
  if (days < 0) return `Expired ${-days} day${days === -1 ? '' : 's'} ago`;
  if (days === 0) return 'Ends today';
  return `Ends in ${days} day${days === 1 ? '' : 's'}`;
}

function leaseTone(device) {
  const days = daysUntil(device.lease_end);
  if (device.ownership !== 'lease' || days === null) return 'muted';
  if (days < 0) return 'bad';
  if (days <= 30) return 'warn';
  return 'good';
}

function Devices() {
  const loadDevices = useCallback(() => deviceService.getAll(), []);
  const {
    data: devices = [],
    loading,
    error: devicesError,
    refresh: refreshDevices,
  } = useAppResource('devices', loadDevices, []);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('all');
  const [bulkOpen, setBulkOpen] = useState(false);

  const visibleError = error || devicesError;
  const kpis = useMemo(() => deviceKpis(devices || []), [devices]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map((f) => [f.id, (devices || []).filter(f.test).length])), [devices]);
  const shown = useMemo(() => {
    const test = (FILTERS.find((f) => f.id === filter) || FILTERS[0]).test;
    return (devices || []).filter(test);
  }, [devices, filter]);

  const openCreateForm = () => {
    setEditingDevice(null);
    setForm(emptyForm);
    setError('');
    setFormOpen(true);
  };

  const openEditForm = (device) => {
    setEditingDevice(device);
    setError('');
    setForm({
      device_id: device.device_id || '',
      label: device.label || '',
      is_active: device.is_active !== false,
      device_type: device.device_type || 'mobile',
      ownership: device.ownership || 'own',
      make_model: device.make_model || '',
      condition_status: device.condition_status || 'working',
      lease_vendor: device.lease_vendor || '',
      lease_start: device.lease_start || '',
      lease_end: device.lease_end || '',
      monthly_rent: device.monthly_rent ?? '',
      notes: device.notes || '',
    });
    setFormOpen(true);
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditingDevice(null);
    setForm(emptyForm);
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (form.ownership === 'lease' && form.lease_start && form.lease_end && form.lease_end < form.lease_start) {
      setError('Lease end is before lease start.');
      return;
    }
    setSaving(true);

    const leased = form.ownership === 'lease';
    const payload = {
      label: form.label.trim() || null,
      is_active: form.is_active,
      device_type: form.device_type,
      ownership: form.ownership,
      make_model: form.make_model.trim() || null,
      condition_status: form.condition_status,
      lease_vendor: leased ? form.lease_vendor.trim() || null : null,
      lease_start: leased ? form.lease_start || null : null,
      lease_end: leased ? form.lease_end || null : null,
      monthly_rent: leased && String(form.monthly_rent).trim() !== '' ? form.monthly_rent : null,
      notes: form.notes.trim() || null,
    };

    try {
      if (editingDevice) {
        await deviceService.update(editingDevice.id, payload);
        toast.success('Device updated', editingDevice.device_id);
      } else {
        await deviceService.create({ device_id: form.device_id.trim(), ...payload });
        toast.success('Device added', form.device_id.trim());
      }

      closeForm();
      await refreshDevices();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (device) => {
    const confirmed = window.confirm(
      `Remove device "${device.device_id}" from the allowed list?`,
    );

    if (!confirmed) {
      return;
    }

    setError('');

    try {
      await deviceService.delete(device.id);
      toast.success('Device removed', device.device_id);
      await refreshDevices();
    } catch (err) {
      toast.error('Device not removed', err.message);
    }
  };

  const columns = [
    {
      headerName: 'Device ID / Serial no.',
      field: 'device_id',
      minWidth: 190,
      cellClass: 'devices-table__device-id',
    },
    { headerName: 'Label', field: 'label', minWidth: 140, valueFormatter: (p) => dash(p.value) },
    {
      headerName: 'Type',
      colId: 'device_type',
      maxWidth: 120,
      valueGetter: (p) => TYPE_SHORT[p.data.device_type] || 'Mobile',
      cellRenderer: (p) => (
        <span className={`devices-chip devices-chip--${p.data.device_type || 'mobile'}`}>{p.value}</span>
      ),
    },
    {
      headerName: 'Make / model',
      field: 'make_model',
      minWidth: 150,
      valueFormatter: (p) => dash(p.value),
    },
    {
      headerName: 'Ownership',
      colId: 'ownership',
      minWidth: 190,
      valueGetter: (p) =>
        p.data.ownership === 'lease'
          ? `Lease ${p.data.lease_vendor || ''} ${leaseText(p.data)}`.trim()
          : 'Own',
      cellRenderer: (p) =>
        p.data.ownership === 'lease' ? (
          <span className="devices-own">
            <span className="devices-chip devices-chip--lease">Lease</span>
            <span
              className="devices-own__text"
              title={[p.data.lease_start && `From ${shortDate(p.data.lease_start)}`, p.data.lease_end && `to ${shortDate(p.data.lease_end)}`, p.data.monthly_rent && `₹${Number(p.data.monthly_rent).toLocaleString('en-IN')} / month`].filter(Boolean).join(' ')}
            >
              {p.data.lease_vendor ? <b>{p.data.lease_vendor}</b> : null}
              <small className={`devices-lease devices-lease--${leaseTone(p.data)}`}>{leaseText(p.data)}</small>
            </span>
          </span>
        ) : (
          <span className="devices-chip devices-chip--own">Own</span>
        ),
    },
    {
      headerName: 'Condition',
      colId: 'condition_status',
      maxWidth: 140,
      valueGetter: (p) => CONDITIONS[p.data.condition_status] || 'Working',
      cellRenderer: (p) => (
        <span className={`devices-cond devices-cond--${p.data.condition_status || 'working'}`}>{p.value}</span>
      ),
    },
    {
      headerName: 'Access',
      colId: 'status',
      valueGetter: (p) => (p.data.is_active ? 'Allowed' : 'Blocked'),
      maxWidth: 120,
      cellRenderer: (p) => (
        <span
          className={`devices-table__status ${
            p.data.is_active ? 'devices-table__status--active' : 'devices-table__status--inactive'
          }`}
        >
          {p.value}
        </span>
      ),
    },
    {
      headerName: 'Last used',
      colId: 'last_scan_at',
      minWidth: 150,
      valueGetter: (p) => (p.data.last_scan_at ? new Date(p.data.last_scan_at) : null),
      comparator: (a, b) => (a?.getTime() || 0) - (b?.getTime() || 0),
      getQuickFilterText: () => '',
      cellRenderer: (p) =>
        p.data.last_scan_at ? (
          <span className="devices-used">
            <span>
              {p.data.active_now ? <span className="devices-used__dot" title="Synced scans in the last 15 minutes" /> : null}
              {lastUsed(p.data.last_scan_at)}
            </span>
            <small>{Number(p.data.scans_30d || 0).toLocaleString()} scans / 30 days</small>
          </span>
        ) : (
          <span className="devices-used devices-used--never">Never</span>
        ),
    },
    {
      headerName: 'Added',
      field: 'created_at',
      filter: 'agDateColumnFilter',
      valueGetter: (p) => (p.data.created_at ? new Date(p.data.created_at) : null),
      valueFormatter: (p) => (p.value ? p.value.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'),
      getQuickFilterText: (p) => (p.value ? p.value.toLocaleString() : ''),
      minWidth: 130,
    },
    actionsColumn((device) => (
      <>
        <button
          type="button"
          className="devices-table__btn devices-table__btn--edit"
          onClick={() => openEditForm(device)}
        >
          Edit
        </button>
        <button
          type="button"
          className="devices-table__btn devices-table__btn--delete"
          onClick={() => handleDelete(device)}
        >
          Delete
        </button>
      </>
    ), { className: 'devices-table__actions' }),
  ];

  const leased = form.ownership === 'lease';

  return (
    <div className="page devices-page">
      <header className="page__header devices-page__header">
        <div>
          <p className="page__eyebrow">Mobile access</p>
          <h1>Devices</h1>
          <p className="devices-page__subtitle">
            Register the phones and HMC scanners allowed to use the mobile audit app, with ownership, lease and condition.
          </p>
        </div>

        <div className="page-header-actions">
          <button type="button" className="bulk-open-btn" onClick={() => setBulkOpen(true)}>
            ⇪ Bulk upload
          </button>
          <button
            type="button"
            className="devices-page__add-btn"
            onClick={openCreateForm}
          >
            + Add Device
          </button>
        </div>
      </header>

      {visibleError && !formOpen && (
        <div className="devices-page__alert" role="alert">
          {visibleError}
        </div>
      )}

      {!loading ? <StatCards items={kpis} /> : null}

      {bulkOpen ? (
        <BulkImportPanel
          open={bulkOpen}
          onClose={() => setBulkOpen(false)}
          title="Bulk upload devices"
          noun="device"
          nounPlural="devices"
          templateName="devices-template.csv"
          intro="Register many phones and HMC scanners at once. Device IDs / serial numbers already registered are skipped. Blank type = Mobile, blank ownership = Own (or Lease when a lease vendor or end date is given), blank condition = Working, blank Allowed = Yes."
          columns={DEVICE_COLUMNS}
          sampleRows={DEVICE_SAMPLE}
          check={checkDeviceRow}
          upload={deviceService.bulkImport}
          onDone={refreshDevices}
        />
      ) : null}

      {!loading && devices.length ? (
        <div className="devices-filters" role="group" aria-label="Show">
          {FILTERS.map((f) => (
            <button key={f.id} type="button" aria-pressed={filter === f.id} onClick={() => setFilter(f.id)}>
              {f.label} <span>{counts[f.id]}</span>
            </button>
          ))}
        </div>
      ) : null}

      <section className="devices-page__card">
        {loading ? (
          <PageLoader label="Loading devices…" />
        ) : devices.length === 0 ? (
          <div className="devices-page__empty-state">
            <p>No devices registered yet.</p>
            <button type="button" onClick={openCreateForm}>
              Add your first device
            </button>
          </div>
        ) : (
          <DataGrid
            rowData={shown}
            columnDefs={columns}
            searchPlaceholder="Search device ID / serial, label, model, vendor…"
          />
        )}
      </section>

      {formOpen && (
        <div className="devices-modal">
          <div className="devices-modal__backdrop" onClick={closeForm} />
          <div className="devices-modal__panel devices-modal__panel--wide" role="dialog" aria-modal="true">
            <div className="devices-modal__header">
              <h2>{editingDevice ? 'Edit Device' : 'Add Device'}</h2>
              <button type="button" onClick={closeForm}>
                ×
              </button>
            </div>

            <form className="devices-form" onSubmit={handleSubmit}>
              {visibleError ? (
                <div className="devices-page__alert" role="alert">
                  {visibleError}
                </div>
              ) : null}

              <div className="devices-form__grid">
                <label>
                  Device ID / Serial no.
                  <input
                    type="text"
                    name="device_id"
                    value={form.device_id}
                    onChange={handleChange}
                    required={!editingDevice}
                    disabled={Boolean(editingDevice)}
                    placeholder="Device ID shown in the app, or the serial number"
                  />
                </label>

                <label>
                  Label (optional)
                  <input
                    type="text"
                    name="label"
                    value={form.label}
                    onChange={handleChange}
                    placeholder="Store scanner 1"
                  />
                </label>
              </div>

              <fieldset className="devices-form__set">
                <legend>Device type</legend>
                <div className="devices-form__choices">
                  {Object.entries(DEVICE_TYPES).map(([value, label]) => (
                    <label key={value} className={form.device_type === value ? 'is-on' : ''}>
                      <input type="radio" name="device_type" value={value} checked={form.device_type === value} onChange={handleChange} />
                      {label}
                    </label>
                  ))}
                </div>
              </fieldset>

              <div className="devices-form__grid">
                <label>
                  Make / model
                  <input type="text" name="make_model" value={form.make_model} onChange={handleChange} placeholder="e.g. Zebra TC26, Samsung A15" maxLength={120} />
                </label>
                <label>
                  Condition
                  <select name="condition_status" value={form.condition_status} onChange={handleChange}>
                    {Object.entries(CONDITIONS).map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <fieldset className="devices-form__set">
                <legend>Ownership</legend>
                <div className="devices-form__choices">
                  {Object.entries(OWNERSHIP).map(([value, label]) => (
                    <label key={value} className={form.ownership === value ? 'is-on' : ''}>
                      <input type="radio" name="ownership" value={value} checked={form.ownership === value} onChange={handleChange} />
                      {value === 'own' ? 'Own (company device)' : `${label} (rented)`}
                    </label>
                  ))}
                </div>
                {leased ? (
                  <div className="devices-form__grid devices-form__lease">
                    <label>
                      Lease vendor
                      <input type="text" name="lease_vendor" value={form.lease_vendor} onChange={handleChange} placeholder="Who it is rented from" maxLength={160} />
                    </label>
                    <label>
                      Monthly rent (₹)
                      <input type="number" name="monthly_rent" min="0" step="0.01" value={form.monthly_rent} onChange={handleChange} placeholder="Optional" />
                    </label>
                    <label>
                      Lease start
                      <input type="date" name="lease_start" value={form.lease_start} onChange={handleChange} />
                    </label>
                    <label>
                      Lease end
                      <input type="date" name="lease_end" value={form.lease_end} min={form.lease_start || undefined} onChange={handleChange} />
                    </label>
                  </div>
                ) : null}
              </fieldset>

              <label>
                Notes (optional)
                <textarea name="notes" rows={2} value={form.notes} onChange={handleChange} maxLength={2000} placeholder="Accessories, damage, who has it…" />
              </label>

              <label className="devices-form__checkbox">
                <input
                  type="checkbox"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
                Allow this device to use the mobile app
              </label>

              <div className="devices-form__actions">
                <button
                  type="button"
                  className="devices-form__cancel"
                  onClick={closeForm}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="devices-form__submit"
                  disabled={saving}
                >
                  {saving
                    ? 'Saving...'
                    : editingDevice
                      ? 'Update Device'
                      : 'Add Device'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Devices;
